import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import api from "@/lib/api";
import { CATEGORY_LABELS, CATEGORY_IMAGES, formatPrice } from "@/lib/constants";
import { useCurrency } from "@/context/CurrencyContext";

export default function Catalog() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const category = params.get("cat") || "";
  const [q, setQ] = useState("");
  const { currency } = useCurrency();

  useEffect(() => {
    setLoading(true);
    const query = {};
    if (category) query.category = category;
    if (q) query.q = q;
    api.get("/products", { params: query })
      .then(({ data }) => setProducts(data))
      .finally(() => setLoading(false));
  }, [category, q]);

  const selectCat = (slug) => {
    if (slug) params.set("cat", slug);
    else params.delete("cat");
    setParams(params);
  };

  return (
    <>
      <AppHeader title="Catálogo" />
      <main className="page-scroll" data-testid="catalog-page">
        <div className="px-4 pt-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="catalog-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="input-clean pl-9"
              placeholder="Buscar programa..."
            />
          </div>
        </div>

        <div className="mt-3 px-4 overflow-x-auto">
          <div className="flex gap-2 pb-1 whitespace-nowrap">
            <button
              onClick={() => selectCat("")}
              data-testid="filter-all"
              className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition ${
                !category ? "bg-[#0A2A66] text-white border-[#0A2A66]" : "bg-white text-[#0A2A66] border-slate-200"
              }`}
            >
              Todos
            </button>
            {Object.entries(CATEGORY_LABELS).map(([slug, label]) => (
              <button
                key={slug}
                data-testid={`filter-${slug}`}
                onClick={() => selectCat(slug)}
                className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition ${
                  category === slug ? "bg-[#0A2A66] text-white border-[#0A2A66]" : "bg-white text-[#0A2A66] border-slate-200"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="px-4 mt-4 grid grid-cols-2 gap-3">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="card-clean p-3 animate-pulse">
                <div className="w-full h-28 bg-slate-100 rounded-xl" />
                <div className="mt-2 h-3 bg-slate-100 rounded w-3/4" />
                <div className="mt-2 h-3 bg-slate-100 rounded w-1/2" />
              </div>
            ))
          ) : products.length === 0 ? (
            <div className="col-span-2 text-center py-10 text-slate-500 text-sm">No hay productos en esta categoría.</div>
          ) : (
            products.map((p) => (
              <Link
                to={`/producto/${p.id}`}
                key={p.id}
                data-testid={`product-card-${p.id}`}
                className="card-clean p-3 flex flex-col hover:border-[#1E5FD9] transition"
              >
                <img
                  src={p.images?.[0] || CATEGORY_IMAGES[p.category]}
                  alt={p.title}
                  className="w-full h-28 object-cover rounded-xl"
                />
                <span className="badge-cat mt-2 self-start">{CATEGORY_LABELS[p.category]}</span>
                <div className="font-heading text-sm font-bold text-[#0A2A66] mt-1.5 line-clamp-2 leading-tight">{p.title}</div>
                <div className="mt-auto pt-2 flex items-center justify-between">
                  <span className="text-[#F5B914] font-bold text-sm">{formatPrice(p.price_usd, currency)}</span>
                </div>
              </Link>
            ))
          )}
        </div>
      </main>
    </>
  );
}
